import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAtomValue, useSetAtom } from 'jotai';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ArrowLeft,
  Save,
  Calendar as CalendarIcon,
  Loader2,
  FileText,
  Users,
  Clock,
  ChevronDown,
  ChevronRight,
  Lightbulb,
  MessageSquare,
  FileCode,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/components/ui/utils';
import { toast } from 'sonner';
import {
  assignmentsAtom,
  assignmentsLoadingAtom,
  assignmentsErrorAtom,
  fetchAssignmentActionAtom,
  updateAssignmentActionAtom,
} from '../stores/assignmentsStore';
import { assignmentsService } from '../services/assignmentsService';
import type {
  AssignmentResponse,
  AssignmentStatus,
  AssignmentPriority,
} from '../types/api';
import { AssignmentEditBreadcrumbs } from '@/components/assignment/edit/AssignmentEditBreadcrumbs';
import { getPriorityColor, getStatusColor } from '@/utils/assignments';

interface AssignmentFormData {
  title: string;
  description: string;
  status: AssignmentStatus;
  priority: AssignmentPriority;
  deadline: Date | null;
  tags: string;
  editorialAngle: string;
  avatarBriefing: string;
  interviewScript: string;
}

export function EditAssignment() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const assignments = useAtomValue(assignmentsAtom);
  const isLoading = useAtomValue(assignmentsLoadingAtom);
  const error = useAtomValue(assignmentsErrorAtom);

  const fetchAssignment = useSetAtom(fetchAssignmentActionAtom);
  const updateAssignment = useSetAtom(updateAssignmentActionAtom);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignment, setAssignment] = useState<AssignmentResponse | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    editorialAngle: false,
    avatarBriefing: false,
    interviewScript: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<AssignmentFormData>();

  const watchedDeadline = watch('deadline');

  // Find assignment from store or fetch it
  useEffect(() => {
    if (!id) return;

    const existingAssignment = assignments.find(a => a.id === id);
    if (existingAssignment) {
      setAssignment(existingAssignment);
      reset({
        title: existingAssignment.title || '',
        description: existingAssignment.description || '',
        status: existingAssignment.status,
        priority: existingAssignment.priority,
        deadline: existingAssignment.deadline
          ? new Date(existingAssignment.deadline)
          : null,
        tags: existingAssignment.tags || '',
        editorialAngle: existingAssignment.editorialAngle || '',
        avatarBriefing: existingAssignment.avatarBriefing || '',
        interviewScript: existingAssignment.interviewScript || '',
      });
    } else {
      // Fetch assignment if not in store
      fetchAssignment(id)
        .then(fetchedAssignment => {
          if (fetchedAssignment) {
            setAssignment(fetchedAssignment);
            reset({
              title: fetchedAssignment.title || '',
              description: fetchedAssignment.description || '',
              status: fetchedAssignment.status,
              priority: fetchedAssignment.priority,
              deadline: fetchedAssignment.deadline
                ? new Date(fetchedAssignment.deadline)
                : null,
              tags: fetchedAssignment.tags || '',
              editorialAngle: fetchedAssignment.editorialAngle || '',
              avatarBriefing: fetchedAssignment.avatarBriefing || '',
              interviewScript: fetchedAssignment.interviewScript || '',
            });
          }
        })
        .catch(console.error);
    }
  }, [id, assignments, fetchAssignment, reset]);

  const onSubmit = async (data: AssignmentFormData) => {
    if (!id || !assignment) return;

    setIsSubmitting(true);
    try {
      await updateAssignment(id, {
        title: data.title,
        description: data.description || null,
        status: data.status,
        priority: data.priority,
        deadline: data.deadline ? data.deadline.toISOString() : null,
        tags: data.tags || null,
        editorialAngle: data.editorialAngle || null,
        avatarBriefing: data.avatarBriefing || null,
        interviewScript: data.interviewScript || null,
      });

      toast.success('Assignment updated successfully');
      navigate('/assignments');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update assignment';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    navigate('/assignments');
  };

  const handleGenerateEditorialAngle = async () => {
    if (!id || !assignment) return;

    try {
      setIsSubmitting(true);
      await assignmentsService.generateAssignmentEditorialAngle(id);

      // Refetch the assignment to get the updated editorial angle
      const updatedAssignment = await assignmentsService.getAssignment(id);
      setAssignment(updatedAssignment);
      setValue('editorialAngle', updatedAssignment.editorialAngle || '');

      toast.success('Editorial angle generated successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate editorial angle';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateAvatarBriefing = async () => {
    if (!id || !assignment) return;

    try {
      setIsSubmitting(true);
      await assignmentsService.generateAvatarBriefing(id);

      // Refetch the assignment to get the updated avatar briefing
      const updatedAssignment = await assignmentsService.getAssignment(id);
      setAssignment(updatedAssignment);
      setValue('avatarBriefing', updatedAssignment.avatarBriefing || '');

      toast.success('Avatar briefing generated successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate avatar briefing';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateInterviewScript = async () => {
    if (!id || !assignment) return;

    try {
      setIsSubmitting(true);
      await assignmentsService.generateInterviewScript(id);

      // Refetch the assignment to get the updated interview script
      const updatedAssignment = await assignmentsService.getAssignment(id);
      setAssignment(updatedAssignment);
      setValue('interviewScript', updatedAssignment.interviewScript || '');

      toast.success('Interview script generated successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to generate interview script';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !assignment) {
    return (
      <div className='h-full flex flex-col'>
        <div className='p-4 border-b'>
          <Skeleton className='h-6 w-48' />
        </div>
        <div className='flex-1 p-4'>
          <div className='space-y-6'>
            <Skeleton className='h-32 w-full' />
            <Skeleton className='h-20 w-full' />
            <Skeleton className='h-20 w-full' />
          </div>
        </div>
      </div>
    );
  }

  if (error || (!isLoading && !assignment)) {
    return <AssignmentEditBreadcrumbs error={error} />;
  }

  if (!assignment) return null;

  return (
    <div className='h-full flex flex-col'>
      {/* Header with Breadcrumbs */}
      <div className='p-4 border-b bg-muted/30'>
        <div className='flex items-center justify-between mb-4'>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to='/assignments'>Assignments</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Edit Assignment</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <Button
            variant='outline'
            size='sm'
            onClick={handleCancel}
            className='gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            Back
          </Button>
        </div>

        <div className='flex items-center gap-3'>
          <h1 className='text-2xl font-bold'>{assignment.title}</h1>
          <Badge className={getPriorityColor(assignment.priority)}>
            {assignment.priority}
          </Badge>
          <Badge className={getStatusColor(assignment.status)}>
            {assignment.status.replace(/_/g, ' ')}
          </Badge>
        </div>

        <div className='flex items-center gap-4 mt-2 text-sm text-muted-foreground'>
          <div className='flex items-center gap-1'>
            <FileText className='h-3 w-3' />
            <span>ID: {assignment.id.slice(0, 8)}...</span>
          </div>
          <div className='flex items-center gap-1'>
            <Users className='h-3 w-3' />
            <span>Source: {assignment.sourceId.slice(0, 8)}...</span>
          </div>
          <div className='flex items-center gap-1'>
            <Clock className='h-3 w-3' />
            <span>
              Created: {format(new Date(assignment.createdAt), 'MMM d, yyyy')}
            </span>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className='flex-1 overflow-auto p-4'>
        <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-2'>
                <Label htmlFor='title'>Title *</Label>
                <Input
                  id='title'
                  {...register('title', { required: 'Title is required' })}
                  className={cn(errors.title && 'border-red-500')}
                />
                {errors.title && (
                  <p className='text-sm text-red-500'>{errors.title.message}</p>
                )}
              </div>

              <div className='space-y-2'>
                <Label htmlFor='description'>Description</Label>
                <Textarea
                  id='description'
                  {...register('description')}
                  rows={4}
                  placeholder='Brief description of the assignment...'
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='priority'>Priority</Label>
                  <Select
                    value={watch('priority')}
                    onValueChange={(value: AssignmentPriority) =>
                      setValue('priority', value, { shouldDirty: true })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='low'>Low</SelectItem>
                      <SelectItem value='medium'>Medium</SelectItem>
                      <SelectItem value='high'>High</SelectItem>
                      <SelectItem value='urgent'>Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className='space-y-2'>
                  <Label>Deadline</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        className={cn(
                          'w-full justify-start text-left font-normal',
                          !watchedDeadline && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className='mr-2 h-4 w-4' />
                        {watchedDeadline ? (
                          format(watchedDeadline, 'PPP')
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-auto p-0'>
                      <Calendar
                        mode='single'
                        selected={watchedDeadline || undefined}
                        onSelect={date =>
                          setValue('deadline', date ?? null, {
                            shouldDirty: true,
                          })
                        }
                        showOutsideDays={false}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-2'>
                <Label htmlFor='tags'>Tags</Label>
                <Input
                  id='tags'
                  {...register('tags')}
                  placeholder='e.g., breaking-news, tech, finance (comma-separated)'
                />
              </div>
            </CardContent>
          </Card>

          {/* Editorial Angle */}
          <Card>
            <Collapsible
              open={expandedSections.editorialAngle}
              onOpenChange={open =>
                setExpandedSections(prev => ({ ...prev, editorialAngle: open }))
              }
            >
              <CollapsibleTrigger asChild className='items-center'>
                <CardHeader className='cursor-pointer hover:bg-muted/50 transition-colors pt-3 pb-2'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <Lightbulb className='h-4 w-4 text-orange-500' />
                      <CardTitle>Editorial Angle</CardTitle>
                      {assignment.editorialAngle && (
                        <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                          <span>•</span>
                          <span>
                            {assignment.editorialAngleGeneratedAt
                              ? 'Generated'
                              : 'Manual'}
                          </span>
                        </div>
                      )}
                    </div>
                    {expandedSections.editorialAngle ? (
                      <ChevronDown className='h-4 w-4' />
                    ) : (
                      <ChevronRight className='h-4 w-4' />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className='pt-4'>
                  <div className='space-y-4'>
                    <div className='flex items-center justify-end'>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='gap-2'
                        onClick={() => handleGenerateEditorialAngle()}
                        disabled={isSubmitting}
                      >
                        <Lightbulb className='h-3 w-3' />
                        Generate
                      </Button>
                    </div>
                    <Textarea
                      id='editorialAngle'
                      {...register('editorialAngle')}
                      rows={6}
                      placeholder='Define the editorial angle and perspective for this story...'
                      className='resize-none'
                    />
                    {assignment.editorialAngleGeneratedAt && (
                      <p className='text-xs text-muted-foreground'>
                        Generated on{' '}
                        {format(
                          new Date(assignment.editorialAngleGeneratedAt),
                          "PPP 'at' p"
                        )}
                      </p>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Avatar Briefing */}
          <Card>
            <Collapsible
              open={expandedSections.avatarBriefing}
              onOpenChange={open =>
                setExpandedSections(prev => ({ ...prev, avatarBriefing: open }))
              }
            >
              <CollapsibleTrigger asChild className='items-center'>
                <CardHeader className='cursor-pointer hover:bg-muted/50 transition-colors pt-3 pb-2'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <MessageSquare className='h-4 w-4 text-blue-500' />
                      <CardTitle>Avatar Briefing</CardTitle>
                      {assignment.avatarBriefing && (
                        <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                          <span>•</span>
                          <span>
                            {assignment.avatarBriefingGeneratedAt
                              ? 'Generated'
                              : 'Manual'}
                          </span>
                        </div>
                      )}
                    </div>
                    {expandedSections.avatarBriefing ? (
                      <ChevronDown className='h-4 w-4' />
                    ) : (
                      <ChevronRight className='h-4 w-4' />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className='pt-4'>
                  <div className='space-y-4'>
                    <div className='flex items-center justify-end'>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='gap-2'
                        onClick={handleGenerateAvatarBriefing}
                        disabled={isSubmitting}
                      >
                        <MessageSquare className='h-3 w-3' />
                        Generate
                      </Button>
                    </div>
                    <Textarea
                      id='avatarBriefing'
                      {...register('avatarBriefing')}
                      rows={6}
                      placeholder='Briefing materials for the interviewer avatar...'
                      className='resize-none'
                    />
                    {assignment.avatarBriefingGeneratedAt && (
                      <p className='text-xs text-muted-foreground'>
                        Generated on{' '}
                        {format(
                          new Date(assignment.avatarBriefingGeneratedAt),
                          "PPP 'at' p"
                        )}
                      </p>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Interview Script */}
          <Card>
            <Collapsible
              open={expandedSections.interviewScript}
              onOpenChange={open =>
                setExpandedSections(prev => ({
                  ...prev,
                  interviewScript: open,
                }))
              }
            >
              <CollapsibleTrigger asChild className='items-center'>
                <CardHeader className='cursor-pointer hover:bg-muted/50 transition-colors pt-3 pb-2'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2'>
                      <FileCode className='h-4 w-4 text-purple-500' />
                      <CardTitle>Interview Script</CardTitle>
                      {assignment.interviewScript && (
                        <div className='flex items-center gap-1 text-xs text-muted-foreground'>
                          <span>•</span>
                          <span>
                            {assignment.interviewScriptGeneratedAt
                              ? 'Generated'
                              : 'Manual'}
                          </span>
                        </div>
                      )}
                    </div>
                    {expandedSections.interviewScript ? (
                      <ChevronDown className='h-4 w-4' />
                    ) : (
                      <ChevronRight className='h-4 w-4' />
                    )}
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className='pt-4'>
                  <div className='space-y-4'>
                    <div className='flex items-center justify-end'>
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='gap-2'
                        onClick={handleGenerateInterviewScript}
                        disabled={isSubmitting}
                      >
                        <FileCode className='h-3 w-3' />
                        Generate
                      </Button>
                    </div>
                    <Textarea
                      id='interviewScript'
                      {...register('interviewScript')}
                      rows={8}
                      placeholder='Interview questions and script for the conversation...'
                      className='resize-none'
                    />
                    {assignment.interviewScriptGeneratedAt && (
                      <p className='text-xs text-muted-foreground'>
                        Generated on{' '}
                        {format(
                          new Date(assignment.interviewScriptGeneratedAt),
                          "PPP 'at' p"
                        )}
                      </p>
                    )}
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>
          {/* Form Actions */}
          <div className='flex items-center justify-end'>
            <Button
              type='submit'
              disabled={isSubmitting || !isDirty}
              className='gap-2'
            >
              {isSubmitting ? (
                <>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  Saving...
                </>
              ) : (
                <>
                  <Save className='h-4 w-4' />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
